import React, { useState, useEffect, useRef } from "react";
import CommitOnBlurNumberInput from "../CommitOnBlurNumberInput";
import { ChromePicker } from "react-color";
import Inventum from "../../../../../../engine/Inventum";

function SwatchColor(props) {
  const colorRef = useRef(null);

  useEffect(() => {
    if (colorRef.current) {
      colorRef.current.style.backgroundColor = props.color;
    }
  }, [props.color]);

  return <div className="ModelPropsSwatchColor" ref={colorRef} />;
}

function ColorSelector(props) {
  const getRgbaValue = (color) => `rgba(${color.rgb.r},${color.rgb.g},${color.rgb.b},${color.rgb.a})`;

  return (
    <div
      className="ModelPropsInlineColorPanel MarkerColorPicker"
      onClick={(e) => { e.stopPropagation(); }}
      onMouseDown={(e) => { e.stopPropagation(); }}
      onTouchStart={(e) => { e.stopPropagation(); }}
    >
      <div className="ModelPropsColorPickerWrap">
        <ChromePicker
          onChange={(color) => {
            props.updateMarker(props.markerKey, getRgbaValue(color), { refresh: false });
          }}
          onChangeComplete={(color) => {
            props.updateMarker(props.markerKey, getRgbaValue(color), { refresh: true });
          }}
          color={props.color}
        />
        <button className="MarkerColorCloseButton" onClick={props.handleClose}>
          <span className="material-icons">close</span>
          Close
        </button>
      </div>
    </div>
  );
}

function ToggleMarkerControl(props) {
  return (
    <div className="MarkerToggleRow">
      <label className="MarkerToggleLabel" title="Marker Visible (Requires Reload)">
        <input 
          type="checkbox" 
          className="MarkerCheckbox"
          checked={props.marker.showMarker} 
          onChange={(e) => props.updateMarker("showMarker", e.target.checked)} 
        />
        <span className="MarkerCheckboxIcon material-icons">
          {props.marker.showMarker ? 'check_box' : 'check_box_outline_blank'}
        </span>
        <span>Show Marker</span>
      </label>
      <div className="MarkerReloadNote">* Requires reload</div>
    </div>
  );
}

function SetOffsetXControl(props) {
  if (!Object.prototype.hasOwnProperty.call(props.marker, "offsetX")) {
    return null;
  }

  const handleCommit = (value) => {
    props.updateMarker("offsetX", value);
  };

  return (
    <div className="MarkerInputRow">
      <label className="MarkerInputLabel">Offset X</label>
      <div className="MarkerInputGroup">
        <CommitOnBlurNumberInput
          className="MarkerNumberInput"
          wrapperClassName="InventumNumberInputControlFill"
          value={props.marker.offsetX}
          onCommit={handleCommit}
          onClick={(e) => e.stopPropagation()}
        />
      </div>
    </div>
  );
}

function SetOffsetYControl(props) {
  if (!Object.prototype.hasOwnProperty.call(props.marker, "offsetY")) {
    return null;
  }

  const handleCommit = (value) => {
    props.updateMarker("offsetY", value);
  };

  return (
    <div className="MarkerInputRow">
      <label className="MarkerInputLabel">Offset Y</label>
      <div className="MarkerInputGroup">
        <CommitOnBlurNumberInput
          className="MarkerNumberInput"
          wrapperClassName="InventumNumberInputControlFill"
          value={props.marker.offsetY}
          onCommit={handleCommit}
          onClick={(e) => e.stopPropagation()}
        />
      </div>
    </div>
  );
}

function SetInactiveBackgroundControl(props) {
  if (!Object.prototype.hasOwnProperty.call(props.marker, "inactiveBackground")) {
    return null;
  }

  return (
    <div className={`MarkerInputRow MarkerColorInputRow${props.activeInput === "inactiveBackground" ? " is-open" : ""}`}>
      <label className="MarkerInputLabel">Inactive Color</label>
      <div className="MarkerColorWrapper">
        <div
          className="ModelPropsSwatch MarkerColorSwatch"
          tabIndex={0}
          onClick={() => {
            props.setActiveInput(props.activeInput === "inactiveBackground" ? null : "inactiveBackground");
          }}
          title="Click to change color"
        >
          <SwatchColor color={props.marker.inactiveBackground} />
          <span className="ModelPropsSwatchHex MarkerColorValue">{props.marker.inactiveBackground}</span>
        </div>
      </div>
      {props.activeInput === "inactiveBackground" ? (
        <ColorSelector
          handleClose={props.clearActiveInput}
          markerKey="inactiveBackground"
          color={props.marker.inactiveBackground}
          updateMarker={props.updateMarker}
          getLabels={props.getLabels}
        />
      ) : null}
    </div>
  );
}

function SetHoverBackgroundControl(props) {
  if (!Object.prototype.hasOwnProperty.call(props.marker, "hoverBackground")) {
    return null;
  }

  return (
    <div className={`MarkerInputRow MarkerColorInputRow${props.activeInput === "hoverBackground" ? " is-open" : ""}`}>
      <label className="MarkerInputLabel">Hover Color</label>
      <div className="MarkerColorWrapper">
        <div
          className="ModelPropsSwatch MarkerColorSwatch"
          tabIndex={0}
          onClick={() => {
            props.setActiveInput(props.activeInput === "hoverBackground" ? null : "hoverBackground");
          }}
          title="Click to change color"
        >
          <SwatchColor color={props.marker.hoverBackground} />
          <span className="ModelPropsSwatchHex MarkerColorValue">{props.marker.hoverBackground}</span>
        </div>
      </div>
      {props.activeInput === "hoverBackground" ? (
        <ColorSelector
          handleClose={props.clearActiveInput}
          markerKey="hoverBackground"
          color={props.marker.hoverBackground}
          updateMarker={props.updateMarker}
          getLabels={props.getLabels}
        />
      ) : null}
    </div>
  );
}

function SetInactiveSizeControl(props) {
  if (!Object.prototype.hasOwnProperty.call(props.marker, "inactiveSize")) {
    return null;
  }

  const handleCommit = (value) => {
    props.updateMarker("inactiveSize", value);
  };

  return (
    <div className="MarkerInputRow">
      <label className="MarkerInputLabel">Inactive Size</label>
      <div className="MarkerInputGroup">
        <CommitOnBlurNumberInput
          className="MarkerNumberInput"
          wrapperClassName="InventumNumberInputControlFill"
          value={props.marker.inactiveSize}
          onCommit={handleCommit}
          onClick={(e) => e.stopPropagation()}
        />
      </div>
    </div>
  );
}

function SetActiveSizeControl(props) {
  if (!Object.prototype.hasOwnProperty.call(props.marker, "hoverSize")) {
    return null;
  }

  const handleCommit = (value) => {
    props.updateMarker("hoverSize", value);
  };

  return (
    <div className="MarkerInputRow">
      <label className="MarkerInputLabel">Hover Size</label>
      <div className="MarkerInputGroup">
        <CommitOnBlurNumberInput
          className="MarkerNumberInput"
          wrapperClassName="InventumNumberInputControlFill"
          value={props.marker.hoverSize}
          onCommit={handleCommit}
          onClick={(e) => e.stopPropagation()}
        />
      </div>
    </div>
  );
}

function MarkerDrawer(props) {
  const hasOffsets = Object.prototype.hasOwnProperty.call(props.marker, "offsetX");
  const hasSizes = Object.prototype.hasOwnProperty.call(props.marker, "inactiveSize");
  const hasColors = Object.prototype.hasOwnProperty.call(props.marker, "inactiveBackground");

  return (
    <div className="MarkerDrawerContainer">
      <div className="MarkerSection">
        <div className="MarkerSectionTitle">
          <span className="material-icons">visibility</span>
          Visibility
        </div>
        <div className="MarkerSectionContent">
          <ToggleMarkerControl {...props} />
        </div>
      </div>
      
      {hasOffsets && (
        <div className="MarkerSection">
          <div className="MarkerSectionTitle">
            <span className="material-icons">open_with</span>
            Position
          </div>
          <div className="MarkerSectionContent MarkerGrid">
            <SetOffsetXControl {...props} />
            <SetOffsetYControl {...props} />
          </div>
        </div>
      )}
      
      {hasSizes && (
        <div className="MarkerSection">
          <div className="MarkerSectionTitle">
            <span className="material-icons">photo_size_select_small</span>
            Size
          </div>
          <div className="MarkerSectionContent MarkerGrid">
            <SetInactiveSizeControl {...props} />
            <SetActiveSizeControl {...props} />
          </div>
        </div>
      )}
      
      {hasColors && (
        <div className="MarkerSection MarkerColorSection">
          <div className="MarkerSectionTitle">
            <span className="material-icons">palette</span>
            Colors
          </div>
          <div className="MarkerSectionContent MarkerGrid MarkerColorGrid">
            <SetInactiveBackgroundControl {...props} />
            <SetHoverBackgroundControl {...props} />
          </div>
        </div>
      )}
    </div>
  );
}

function MarkerPanel(props) {
  const [open, setOpen] = useState(false);
  const [activeInput, setActiveInput] = useState(null);
  const [marker, setMarker] = useState(props.marker);

  useEffect(() => {
    setMarker(props.marker);
  }, [props.marker]);

  const updateMarker = (key, value, options = {}) => {
    const { refresh = true } = options;
    const newMarker = JSON.parse(JSON.stringify(marker));

    if (key !== undefined && value !== undefined) {
      newMarker[key] = value;
    }

    setMarker(newMarker);
    Inventum.labels.setMarkerStyle(props.id, newMarker);
    if (refresh) {
      props.getLabels();
    }
  };

  const toggleOpen = () => {
    setOpen(!open);
  };

  const clearActiveInput = () => {
    setActiveInput(null);
    props.getLabels();
  };

  return (
    <div>
      <div className="EditorDrawerItemHeader" onClick={toggleOpen}>
        <span className="PanelOpenStatus">{open ? "▾" : "▸"}</span>
        <span>Marker</span>
      </div>
      {open && (
        <MarkerDrawer
          clearActiveInput={clearActiveInput}
          setActiveInput={setActiveInput}
          activeInput={activeInput}
          getLabels={props.getLabels}
          updateMarker={updateMarker}
          marker={marker}
        />
      )}
    </div>
  );
}

export { MarkerPanel };
